/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachStateProvider, useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import type { QuickPrompt } from 'bundles/wes-common/components/WESAskCoursera/WESAskCourseraEmptyState';
import WESAskCourseraLauncher from 'bundles/wes-common/components/WESAskCoursera/WESAskCourseraLauncher';
import WESAskCourseraPopup from 'bundles/wes-common/components/WESAskCoursera/WESAskCourseraPopup';
import { CONTEXT_CONFIG, WES_COACH_MODE_ID } from 'bundles/wes-common/components/WESAskCoursera/constants';

type Position = 'bottom-right' | 'bottom-left';

const styles = {
  // Hide on mobile screens - only show on tablet and desktop
  container: css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

export type WESAskCourseraProps = {
  position?: Position;
  disabled?: boolean;
  autoOpenDelay?: number; // Delay in seconds before auto-opening (e.g., 3 for 3 seconds). Only opens once per session.
  quickPrompts?: QuickPrompt[];
  onOpen?: () => void;
  onClose?: () => void;
  welcomeMessage?: string;
  launcherVariant?: 'default' | 'inverted';
};

/**
 * Content component that consumes CoachStateProvider context.
 * Must be rendered inside CoachStateProvider.
 */
const WESAskCourseraContent = ({
  position = 'bottom-right',
  disabled = false,
  autoOpenDelay,
  quickPrompts,
  onOpen,
  onClose,
  welcomeMessage,
  launcherVariant = 'default',
}: WESAskCourseraProps) => {
  const { coachOpen, setCoachOpen, setContextConfig } = useCoachState();
  const hasAutoOpened = useRef(false);
  const track = useTracker();

  // Set context config on mount so CourseraAiHeader can access it
  useEffect(() => {
    setContextConfig(CONTEXT_CONFIG);
  }, [setContextConfig]);

  const toggleCoach = React.useCallback<() => void>(() => {
    if (disabled) return;

    setCoachOpen((prev) => {
      const nextState = !prev;

      track('interact_coach', {
        coachInteractionType: nextState ? 'launch_coach_tool' : 'close_coach_tool',
        coachAction: 'Ask Coursera Icon',
        coach: {
          mode: WES_COACH_MODE_ID,
        },
      });

      if (nextState) {
        onOpen?.();
        hasAutoOpened.current = true;
      } else {
        onClose?.();
      }

      return nextState;
    });
  }, [disabled, onOpen, setCoachOpen, onClose, track]);

  // Auto-open functionality - only opens once per session
  useEffect(() => {
    if (!autoOpenDelay || disabled || hasAutoOpened.current) return undefined;

    const timer = setTimeout(() => {
      setCoachOpen((currentIsOpen) => {
        if (currentIsOpen) return currentIsOpen;

        track('interact_coach', {
          coachInteractionType: 'launch_coach_tool',
          coach: {
            mode: WES_COACH_MODE_ID,
          },
          coachAction: 'auto_open_coach_tool',
        });

        onOpen?.();
        hasAutoOpened.current = true;

        return true;
      });
    }, autoOpenDelay * 1000);

    return () => clearTimeout(timer);
  }, [autoOpenDelay, disabled, onOpen, setCoachOpen, track]);

  if (disabled) {
    return null;
  }

  return (
    <>
      <WESAskCourseraLauncher
        colorVariant={launcherVariant}
        isOpen={coachOpen}
        onClick={toggleCoach}
        position={position}
      />
      {coachOpen && (
        <WESAskCourseraPopup
          onClose={toggleCoach}
          contextConfig={CONTEXT_CONFIG}
          position={position}
          quickPrompts={quickPrompts}
          welcomeMessage={welcomeMessage}
        />
      )}
    </>
  );
};

/**
 * WESAskCoursera - AI Coach component for WES applications (teams-v2, ent-website).
 * Wraps the inner component with CoachStateProvider to manage state.
 * Note: Hidden on mobile screens - only visible on tablet and desktop.
 */
const WESAskCoursera = (props: WESAskCourseraProps) => {
  return (
    <div css={styles.container}>
      <CoachStateProvider>
        <WESAskCourseraContent {...props} />
      </CoachStateProvider>
    </div>
  );
};

export default WESAskCoursera;
